import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * React.lazy with a retry, for the chunks of a deployed single page app.
 *
 * A chunk request can fail for two reasons that a user cannot act on: the tab was opened
 * before a deploy and asks for a file that no longer exists, or the network dropped the
 * request. React.lazy then rejects for good, the Suspense boundary renders nothing and the
 * page looks empty until a manual refresh.
 *
 * The import is retried once after a short delay; if it still fails and the page was not
 * reloaded for that reason yet, the page is reloaded once to pick up the current index.html.
 */

const RELOAD_FLAG_PREFIX = 'chunk-reload:';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const readFlag = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (key: string, value: boolean) => {
  try {
    if (value) window.sessionStorage.setItem(key, '1');
    else window.sessionStorage.removeItem(key);
  } catch {
    /* private mode or blocked storage: the retry above still applies */
  }
};

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  name: string,
): LazyExoticComponent<T> {
  const flagKey = `${RELOAD_FLAG_PREFIX}${name}`;

  return lazy(async () => {
    try {
      const module = await factory();
      writeFlag(flagKey, false);
      return module;
    } catch (error) {
      console.warn(`Chunk "${name}" failed to load, retrying`, error);
      await delay(400);
      try {
        const module = await factory();
        writeFlag(flagKey, false);
        return module;
      } catch (retryError) {
        // A stale index.html points at files that no longer exist: one reload fixes it.
        if (!readFlag(flagKey)) {
          writeFlag(flagKey, true);
          window.location.reload();
          // Never resolves: the page is going away.
          return new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}

export default lazyWithRetry;
